import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import IdCard from '@/components/IdCard';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const STATUS_COLOR = { pending: '#CCFF00', approved: '#007AFF', selected: '#30D158', rejected: '#FF3B30' };

export default function Track() {
  const [appNo, setAppNo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const search = useLocation().search;

  useEffect(() => {
    const q = new URLSearchParams(search).get('app');
    if (q) { setAppNo(q); lookup(q); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const lookup = async (val) => {
    const code = (val || appNo || '').trim();
    if (!code) return;
    setLoading(true); setData(null);
    try {
      const { data } = await api.get(`/registrations/track/${code}`);
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Not found'); }
    finally { setLoading(false); }
  };

  return (
    <main className="min-h-screen bg-[#0A0A0A] text-white pt-28 pb-24 grain" data-testid="track-page">
      <div className="mx-auto max-w-3xl px-6">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#CCFF00] mb-3">Track Application</p>
        <h1 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight mb-2">Where&apos;s my application?</h1>
        <p className="text-white/60">Enter your Application Number to see status and download your ID card.</p>

        <div className="mt-8 border border-white/10 bg-[#141414] p-5 flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Label className="text-[10px] font-mono uppercase tracking-widest text-white/60">Application No.</Label>
            <Input value={appNo} onChange={(e) => setAppNo(e.target.value.toUpperCase())} placeholder="PSF2026-1001" className="bg-black border-white/20 rounded-none mt-1 font-mono" data-testid="track-input" />
          </div>
          <button onClick={() => lookup()} disabled={loading} className="bg-[#CCFF00] text-black px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-white disabled:opacity-50 sm:self-end" data-testid="track-btn">
            {loading ? 'Searching…' : 'Track'}
          </button>
        </div>

        {data && (
          <div className="mt-8" data-testid="track-result">
            <div className="border border-white/10 bg-[#141414] p-5 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">Application</p>
                <p className="font-display text-2xl">{data.application_no}</p>
                <p className="font-heading text-base text-white/80 mt-1">{data.full_name} · {data.sport}</p>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">Status</p>
                <p className="font-display text-2xl uppercase" style={{ color: STATUS_COLOR[data.status] || '#fff' }}>{data.status}</p>
              </div>
            </div>
            {data.remarks && <p className="mt-3 text-sm text-white/70 border-l-2 border-[#CCFF00] pl-3"><b>Admin remarks:</b> {data.remarks}</p>}
            {data.status === 'pending' && <p className="mt-5 text-sm text-white/60">Your payment is being verified. You&apos;ll be notified by email once approved.</p>}
            {data.status === 'rejected' && <p className="mt-5 text-sm text-white/60">Your application has been rejected. Contact the federation for details.</p>}
            {(data.status === 'approved' || data.status === 'selected') && (
              <>
                <div className="mt-6 flex items-center gap-3">
                  <button onClick={() => setShowPrint((v) => !v)} className="bg-[#CCFF00] text-black px-5 py-3 font-mono text-xs uppercase tracking-widest hover:bg-white" data-testid="toggle-card">
                    {showPrint ? 'Hide ID Card' : 'View ID Card'}
                  </button>
                  <button onClick={() => { setShowPrint(true); setTimeout(() => window.print(), 300); }} className="border border-white/20 px-5 py-3 font-mono text-xs uppercase tracking-widest hover:bg-white hover:text-black" data-testid="print-card">
                    Print ID Card
                  </button>
                </div>
                {showPrint && <div className="mt-6"><IdCard data={data} /></div>}
              </>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
